import { MenuAlt2Icon } from '@heroicons/react/outline';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { MobileSidebar } from './components/MobileSidebar';
import { Sidebar } from './components/Sidebar';
import { UserNavigation } from './components/UserNavigation';
import { MdOpenInNew } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import logo from 'assets/logo-sm.png';
import { useLocation } from 'react-router-dom';

export const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  /** REALTIME CLOCK */
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const formattedDate = now.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const formattedTime = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
      {/* MOBILE SIDEBAR */}
      <MobileSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* DESKTOP SIDEBAR */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* MAIN CONTENT WRAPPER */}
      <div className="flex flex-col flex-1 overflow-hidden relative">
        {/* HEADER */}
        <motion.header
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="
            h-16 sticky top-0 z-50
            backdrop-blur-xl bg-white/70 border-b border-white/20
            shadow-[0_4px_20px_rgba(0,0,0,0.04)]
            flex items-center justify-between
            px-4 md:px-8
          "
        >
          <div className="flex items-center gap-4">
            {/* MOBILE MENU */}
            <button
              className="
                md:hidden p-2 rounded-xl bg-white/60 backdrop-blur
                hover:bg-white/80 active:scale-95 transition shadow-sm
              "
              onClick={() => setSidebarOpen(true)}
            >
              <MenuAlt2Icon className="h-6 w-6 text-gray-700" />
            </button>

            {/* REAL-TIME DATE & TIME */}
            <div className="flex flex-col leading-tight">
              <span className="text-gray-900 font-semibold text-sm md:text-base">
                {formattedTime}
              </span>
              <span className="text-gray-500 text-xs md:text-sm">{formattedDate}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-5">
            {/* TAWK LINK */}
            <a
              href="https://dashboard.tawk.to/login"
              target="_blank"
              rel="noreferrer"
              className="
                hidden sm:flex px-3 py-1.5 rounded-xl items-center gap-2
                bg-gray-900 text-white text-xs md:text-sm
                hover:bg-gray-800 active:scale-95 transition shadow-sm
              "
            >
              <MdOpenInNew size={16} /> Tawk
            </a>

            {/* USER MENU */}
            <UserNavigation />
          </div>
        </motion.header>

        {/* CONTENT */}
        <main className="relative flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="p-4 md:p-6 min-h-[85vh]"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* FOOTER */}
        <footer
          className="
            px-4 py-4 bg-white/70 backdrop-blur-xl border-t border-white/20
            text-gray-600 text-xs md:text-sm flex items-center justify-between
            shadow-[0_-4px_20px_rgba(0,0,0,0.05)]
          "
        >
          <p className="leading-tight">
            © Ayinza Qreatives {currentYear} — Powered by{' '}
            <a
              href="https://ashitacreatives.com"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Ashita Creatives
            </a>
          </p>

          <img src={logo} alt="Logo" className="h-6 opacity-80" />
        </footer>
      </div>
    </div>
  );
};

MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
};
